import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { buildBookingCompletionNavigationState } from "../../utils/publicBookingStatus";

const API_URL = window.location.hostname === "localhost"
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173")
  : window.location.origin;

const DISPLAY_TIMEZONE = "Asia/Tokyo";
const dayLabels = ["日", "月", "火", "水", "木", "金", "土"];

function toDateInputValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatMonthLabel(date) {
  return date.toLocaleDateString("ja-JP", { year: "numeric", month: "long" });
}

function formatDayLabel(date) {
  return date.toLocaleDateString("ja-JP", { month: "numeric", day: "numeric", weekday: "short" });
}

function formatDateTime(value) {
  if (!value) return "未設定";
  return new Date(value).toLocaleString("ja-JP", { timeZone: DISPLAY_TIMEZONE });
}

function isFutureSlot(slot) {
  const slotStart = new Date(slot?.start_at);
  return !Number.isNaN(slotStart.getTime()) && slotStart.getTime() > Date.now();
}

function readCookie(name) {
  if (typeof document === "undefined") return "";
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
}

function writeCookie(name, value, days = 90) {
  if (typeof document === "undefined" || !value) return;
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function getOrCreateBookingVisitorId() {
  const existing = readCookie("sb_vid") || readCookie("sb_visitor_id") || readCookie("visitor_cookie_id");
  if (existing) return existing;
  const generated = typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  writeCookie("sb_vid", generated);
  return generated;
}

function collectUrlParams() {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  return Array.from(params.entries()).reduce((acc, [key, value]) => {
    acc[key] = value;
    return acc;
  }, {});
}

function persistTrackingParamsFromUrl() {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  ["sb_tid", "tracking_id", "lid"].forEach((key) => {
    const value = params.get(key);
    if (value) writeCookie(key, value, 90);
  });
}

function buildTrackingContext(pageLoadedAt) {
  const urlParams = collectUrlParams();
  return {
    page_loaded_at: new Date(pageLoadedAt).toISOString(),
    submission_elapsed_ms: Math.max(0, Date.now() - pageLoadedAt),
    submission_url: typeof window === "undefined" ? "" : window.location.href,
    landing_url: typeof window === "undefined" ? "" : window.location.href,
    source_path: typeof window === "undefined" ? "" : `${window.location.pathname}${window.location.search}`,
    referrer: typeof document === "undefined" ? "" : document.referrer,
    visitor_cookie_id: getOrCreateBookingVisitorId(),
    tracking_id: urlParams.sb_tid || urlParams.tracking_id || readCookie("sb_tid") || readCookie("tracking_id"),
    lead_id: urlParams.lid || readCookie("lid"),
    url_params: urlParams,
    language: typeof navigator === "undefined" ? "" : navigator.language,
    timezone_offset_minutes: typeof Date === "undefined" ? null : new Date().getTimezoneOffset(),
    screen_width: typeof window === "undefined" ? null : window.screen?.width,
    screen_height: typeof window === "undefined" ? null : window.screen?.height
  };
}

function buildCalendarDays(currentMonth) {
  const start = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const end = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
  const startWeekday = start.getDay();
  const daysInMonth = end.getDate();
  const cells = [];

  for (let i = 0; i < startWeekday; i += 1) {
    cells.push(null);
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day));
  }

  return cells;
}

function Field({ label, required = false, children }) {
  return (
    <label className="block">
      <span className="mb-2 flex items-center gap-2 text-sm font-bold text-gray-700">
        <span>{label}</span>
        {required ? <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.16em] text-rose-600">必須</span> : null}
      </span>
      {children}
    </label>
  );
}

function StepBadge({ number, label }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-black uppercase tracking-[0.18em] text-blue-700">
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-[11px] text-white">{number}</span>
      <span>{label}</span>
    </div>
  );
}

export default function PublicBookingPage() {
  const { publicId, appointmentPublicId } = useParams();
  const navigate = useNavigate();
  const isRescheduleMode = Boolean(appointmentPublicId);
  const today = useMemo(() => new Date(), []);
  const [pageLoadedAt] = useState(() => Date.now());
  const [bookingLink, setBookingLink] = useState(null);
  const [originalAppointment, setOriginalAppointment] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(toDateInputValue(today));
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slotLoading, setSlotLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    invitee_name: "",
    invitee_email: "",
    invitee_phone: "",
    invitee_company: "",
    note: "",
    booking_hp: ""
  });

  useEffect(() => {
    persistTrackingParamsFromUrl();
    getOrCreateBookingVisitorId();
  }, []);

  useEffect(() => {
    let ignore = false;

    async function bootstrap() {
      setLoading(true);
      setError("");

      try {
        const [linkRes, appointmentRes] = await Promise.all([
          fetch(`${API_URL}/api/public/booking/links/${encodeURIComponent(publicId)}`),
          isRescheduleMode
            ? fetch(`${API_URL}/api/public/booking/appointments/${encodeURIComponent(appointmentPublicId)}`)
            : Promise.resolve(null)
        ]);

        const linkJson = await linkRes.json();
        if (!linkRes.ok) {
          throw new Error(linkJson.error || "予約リンクの取得に失敗しました。");
        }

        let appointmentJson = null;
        if (appointmentRes) {
          appointmentJson = await appointmentRes.json();
          if (!appointmentRes.ok) {
            throw new Error(appointmentJson.error || "変更対象の予約取得に失敗しました。");
          }
        }

        if (!ignore) {
          setBookingLink(linkJson.booking_link);
          if (appointmentJson?.appointment) {
            const appointment = appointmentJson.appointment;
            setOriginalAppointment(appointment);
            setForm({
              invitee_name: appointment.invitee_name || "",
              invitee_email: appointment.invitee_email || "",
              invitee_phone: appointment.invitee_phone || "",
              invitee_company: appointment.invitee_company || "",
              note: appointment.note || "",
              booking_hp: ""
            });
            if (appointment.started_at) {
              const originalDate = new Date(appointment.started_at);
              setCurrentMonth(new Date(originalDate.getFullYear(), originalDate.getMonth(), 1));
              setSelectedDate(toDateInputValue(originalDate));
            }
          }
        }
      } catch (fetchError) {
        if (!ignore) {
          setError(fetchError.message || "初期化に失敗しました。");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    bootstrap();
    return () => {
      ignore = true;
    };
  }, [appointmentPublicId, isRescheduleMode, publicId]);

  useEffect(() => {
    if (!bookingLink || !selectedDate) return;

    let ignore = false;
    async function fetchSlots() {
      setSlotLoading(true);
      setError("");

      try {
        const params = new URLSearchParams({
          date: selectedDate,
          display_timezone: DISPLAY_TIMEZONE
        });
        const res = await fetch(`${API_URL}/api/public/booking/links/${encodeURIComponent(publicId)}/availability?${params.toString()}`);
        const json = await res.json();
        if (!res.ok) {
          throw new Error(json.error || "空き枠の取得に失敗しました。");
        }

        if (!ignore) {
          const nextSlots = Array.isArray(json.slots) ? json.slots.filter(isFutureSlot) : [];
          setSlots(nextSlots);
          if (isRescheduleMode && originalAppointment?.started_at) {
            const existingSlot = nextSlots.find((slot) => slot.start_at === originalAppointment.started_at);
            setSelectedSlot(existingSlot || null);
          } else {
            setSelectedSlot(null);
          }
        }
      } catch (fetchError) {
        if (!ignore) {
          setError(fetchError.message || "空き枠の取得に失敗しました。");
          setSlots([]);
        }
      } finally {
        if (!ignore) {
          setSlotLoading(false);
        }
      }
    }

    fetchSlots();
    return () => {
      ignore = true;
    };
  }, [bookingLink, isRescheduleMode, originalAppointment?.started_at, publicId, selectedDate]);

  const selectedSlotLabel = useMemo(() => {
    if (!selectedSlot) return "未選択";
    return `${new Date(selectedSlot.start_at).toLocaleString("ja-JP", { timeZone: DISPLAY_TIMEZONE })} - ${new Date(selectedSlot.end_at).toLocaleTimeString("ja-JP", { timeZone: DISPLAY_TIMEZONE, hour: "2-digit", minute: "2-digit" })}`;
  }, [selectedSlot]);

  const monthCells = useMemo(() => buildCalendarDays(currentMonth), [currentMonth]);
  const selectedDateTime = useMemo(() => new Date(`${selectedDate}T00:00:00`), [selectedDate]);
  const todayValue = useMemo(() => toDateInputValue(today), [today]);
  const maxSelectableDateValue = useMemo(() => {
    const maxDays = Number.parseInt(bookingLink?.max_days_in_future || "30", 10);
    const boundedDays = Number.isInteger(maxDays) ? Math.min(Math.max(maxDays, 1), 365) : 30;
    const maxDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    maxDate.setDate(maxDate.getDate() + boundedDays);
    return toDateInputValue(maxDate);
  }, [bookingLink?.max_days_in_future, today]);

  useEffect(() => {
    if (!bookingLink) return;
    if (selectedDate < todayValue || selectedDate > maxSelectableDateValue) {
      setSelectedDate(todayValue);
    }
  }, [bookingLink, maxSelectableDateValue, selectedDate, todayValue]);

  const handleChange = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedSlot) {
      setError("時間枠を選択してください。");
      return;
    }

    if (!form.invitee_name.trim()) {
      setError("お名前を入力してください。");
      return;
    }
    if (!form.invitee_email.trim()) {
      setError("メールアドレスを入力してください。");
      return;
    }
    if (!form.invitee_company.trim()) {
      setError("会社名を入力してください。");
      return;
    }
    if (!form.invitee_phone.trim()) {
      setError("電話番号を入力してください。");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const endpoint = isRescheduleMode
        ? `${API_URL}/api/public/booking/appointments/${encodeURIComponent(appointmentPublicId)}/reschedule`
        : `${API_URL}/api/public/booking/appointments`;
      const payload = {
        booking_link_public_id: publicId,
        started_at: selectedSlot.start_at,
        ended_at: selectedSlot.end_at,
        timezone: DISPLAY_TIMEZONE,
        submission_elapsed_ms: Math.max(0, Date.now() - pageLoadedAt),
        tracking_context: buildTrackingContext(pageLoadedAt),
        ...form
      };

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.error || (isRescheduleMode ? "日程変更に失敗しました。" : "予約作成に失敗しました。"));
      }

      navigate(`/public/booking/${publicId}/complete/${json.appointment.public_id}`, {
        replace: true,
        state: buildBookingCompletionNavigationState(json)
      });
    } catch (submitError) {
      setError(submitError.message || (isRescheduleMode ? "日程変更に失敗しました。" : "予約作成に失敗しました。"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_#e0f2fe,_transparent_32%),linear-gradient(180deg,_#f8fafc_0%,_#eef2ff_100%)] px-3 py-4 sm:px-4 sm:py-8">
      <div className="mx-auto max-w-7xl space-y-4 sm:space-y-6">
        <header className="rounded-[28px] bg-white/92 p-5 shadow-sm ring-1 ring-white/60 backdrop-blur sm:rounded-[32px] sm:p-6">
          <p className="text-sm font-semibold text-blue-600">Online Booking</p>
          <h1 className="mt-2 text-2xl font-bold text-gray-900 sm:text-3xl">
            {isRescheduleMode ? "予約日時を変更する" : (bookingLink?.title || "日程調整")}
          </h1>
          <p className="mt-3 text-sm leading-6 text-gray-600">
            {isRescheduleMode
              ? "現在の予約内容を引き継いだまま、新しい日時へ変更できます。"
              : (bookingLink?.description || "ご都合の良い日時を選択してください。")}
          </p>
          <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400 sm:text-xs">
            Timezone: {DISPLAY_TIMEZONE}
          </p>
        </header>

        {isRescheduleMode && originalAppointment ? (
          <section className="rounded-[28px] border border-white/70 bg-white/92 p-4 shadow-sm backdrop-blur sm:rounded-[32px] sm:p-6">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-400">Current Appointment</p>
            <div className="mt-3 grid gap-4 md:grid-cols-3">
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                <p className="text-xs font-black uppercase tracking-[0.16em] text-gray-400">現在の日時</p>
                <p className="mt-2 text-sm font-bold text-gray-900">{formatDateTime(originalAppointment.started_at)}</p>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                <p className="text-xs font-black uppercase tracking-[0.16em] text-gray-400">予約者</p>
                <p className="mt-2 text-sm font-bold text-gray-900">{originalAppointment.invitee_name}</p>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                <p className="text-xs font-black uppercase tracking-[0.16em] text-gray-400">操作</p>
                <Link to={`/public/booking/${publicId}/manage/${appointmentPublicId}`} className="mt-2 inline-block text-sm font-bold text-blue-600 hover:text-blue-700">
                  予約管理へ戻る
                </Link>
              </div>
            </div>
          </section>
        ) : null}

        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="grid gap-4 xl:grid-cols-[1.2fr_0.85fr] xl:gap-6">
          <section className="rounded-[28px] border border-white/70 bg-white/92 p-4 shadow-sm backdrop-blur sm:rounded-[32px] sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <StepBadge number="1" label="Date" />
                <h2 className="mt-3 text-lg font-bold text-gray-900">日付を選択</h2>
                <p className="mt-1 text-sm text-gray-500">Google カレンダー感覚で空いている日を選ぶイメージです。</p>
              </div>
              <div className="flex items-center justify-between gap-2 sm:justify-start">
                <button
                  type="button"
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
                  className="rounded-full border border-gray-200 px-3 py-2 text-sm font-bold text-gray-700 transition hover:bg-gray-50"
                >
                  前月
                </button>
                <p className="min-w-[132px] text-center text-sm font-bold text-gray-900 sm:min-w-[140px]">{formatMonthLabel(currentMonth)}</p>
                <button
                  type="button"
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
                  className="rounded-full border border-gray-200 px-3 py-2 text-sm font-bold text-gray-700 transition hover:bg-gray-50"
                >
                  次月
                </button>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-7 gap-1.5 text-center text-[10px] font-black uppercase tracking-[0.14em] text-gray-400 sm:gap-2 sm:text-xs sm:tracking-[0.18em]">
              {dayLabels.map((label) => (
                <div key={label} className="py-2">{label}</div>
              ))}
            </div>

            <div className="mt-2 grid grid-cols-7 gap-1.5 sm:gap-2">
              {monthCells.map((date, index) => {
                if (!date) {
                  return <div key={`blank-${index}`} className="aspect-square rounded-xl bg-transparent sm:rounded-2xl" />;
                }

                const value = toDateInputValue(date);
                const isSelected = value === selectedDate;
                const isPast = value < todayValue;
                const isBeyondBookingWindow = bookingLink && value > maxSelectableDateValue;
                const isToday = value === toDateInputValue(today);

                return (
                  <button
                    key={value}
                    type="button"
                    disabled={isPast || isBeyondBookingWindow}
                    onClick={() => setSelectedDate(value)}
                    className={`aspect-square min-h-[44px] rounded-xl border text-sm font-bold transition sm:rounded-2xl ${
                      isSelected
                        ? "border-blue-600 bg-blue-600 text-white shadow-sm"
                        : isPast || isBeyondBookingWindow
                          ? "cursor-not-allowed border-gray-100 bg-gray-50 text-gray-300"
                          : "border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50"
                    }`}
                  >
                    <span className="block text-sm sm:text-base">{date.getDate()}</span>
                    <span className={`mt-0.5 block text-[9px] font-semibold sm:mt-1 sm:text-[10px] ${isSelected ? "text-blue-100" : isToday ? "text-blue-600" : "text-gray-400"}`}>
                      {isToday ? "Today" : ""}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="rounded-[28px] border border-white/70 bg-white/92 p-4 shadow-sm backdrop-blur sm:rounded-[32px] sm:p-6">
            <StepBadge number="2" label="Time" />
            <h2 className="mt-3 text-lg font-bold text-gray-900">時間を選択</h2>
            <p className="mt-1 text-sm text-gray-500">{formatDayLabel(selectedDateTime)} の空き枠です。</p>

            <div className="mt-4 rounded-2xl border border-gray-200 bg-gray-50 p-4 sm:mt-5">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-400">選択中の日付</p>
              <p className="mt-2 text-lg font-bold text-gray-900">{formatDayLabel(selectedDateTime)}</p>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 sm:mt-5 sm:grid-cols-2">
              {slotLoading ? <p className="col-span-full text-sm text-gray-500">空き枠を読み込み中です。</p> : null}
              {!slotLoading && slots.length === 0 ? <p className="col-span-full text-sm text-gray-500">この日の空き枠はありません。</p> : null}
              {slots.map((slot) => {
                const label = new Date(slot.start_at).toLocaleTimeString("ja-JP", { timeZone: DISPLAY_TIMEZONE, hour: "2-digit", minute: "2-digit" });
                const isActive = selectedSlot?.start_at === slot.start_at;
                return (
                  <button
                    key={slot.start_at}
                    type="button"
                    onClick={() => setSelectedSlot(slot)}
                    className={`min-h-[56px] rounded-2xl border px-4 py-4 text-left text-sm font-bold transition ${
                      isActive
                        ? "border-blue-600 bg-blue-600 text-white shadow-sm"
                        : "border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50"
                    }`}
                  >
                    <span className="block text-base">{label}</span>
                    <span className={`mt-1 block text-xs ${isActive ? "text-blue-100" : "text-gray-400"}`}>予約可能</span>
                  </button>
                );
              })}
            </div>
          </section>
        </div>

        <section className="rounded-[28px] border border-white/70 bg-white/92 p-4 shadow-sm backdrop-blur sm:rounded-[32px] sm:p-6">
          <div className="grid gap-4 xl:grid-cols-[0.8fr_1.2fr] xl:gap-6">
            <div className="rounded-3xl border border-gray-200 bg-gray-50 p-5 xl:sticky xl:top-6 xl:self-start">
              <StepBadge number="3" label="Form" />
              <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-400">選択内容</p>
              <p className="mt-3 text-lg font-bold text-gray-900">{selectedSlotLabel}</p>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="hidden" aria-hidden="true">
                <label>
                  Website
                  <input
                    tabIndex="-1"
                    autoComplete="off"
                    value={form.booking_hp}
                    onChange={handleChange("booking_hp")}
                  />
                </label>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="お名前" required>
                  <input value={form.invitee_name} onChange={handleChange("invitee_name")} required className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500" />
                </Field>
                <Field label="メールアドレス" required>
                  <input type="email" value={form.invitee_email} onChange={handleChange("invitee_email")} required className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500" />
                </Field>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="電話番号" required>
                  <input value={form.invitee_phone} onChange={handleChange("invitee_phone")} required className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500" />
                </Field>
                <Field label="会社名" required>
                  <input value={form.invitee_company} onChange={handleChange("invitee_company")} required className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500" />
                </Field>
              </div>

              <Field label="補足メモ">
                <textarea value={form.note} onChange={handleChange("note")} rows="4" className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500" />
              </Field>

              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  type="submit"
                  disabled={loading || submitting}
                  className="w-full min-h-[56px] rounded-2xl bg-gray-900 px-4 py-3 text-base font-bold text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  {submitting ? (isRescheduleMode ? "変更中..." : "予約を作成中...") : (isRescheduleMode ? "3. 日程変更を確定する" : "3. 予約を確定する")}
                </button>
                {isRescheduleMode ? (
                  <Link
                    to={`/public/booking/${publicId}/manage/${appointmentPublicId}`}
                    className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-center text-sm font-bold text-gray-700 transition hover:bg-gray-50"
                  >
                    管理ページへ戻る
                  </Link>
                ) : null}
              </div>
            </form>
          </div>
        </section>
      </div>

      <div className="sticky bottom-3 z-20 xl:hidden">
        <div className="rounded-3xl border border-white/70 bg-white/95 px-4 py-3 shadow-lg backdrop-blur">
          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-gray-400">Selected</p>
          <p className="mt-1 truncate text-sm font-bold text-gray-900">{selectedSlotLabel}</p>
        </div>
      </div>
    </div>
  );
}
